/**
 * Heuristic firstname.lastname@domain (must stay aligned with backend/src/utils/creativedockEmailGuess.ts).
 */

const LATIN1: Record<string, string> = {
  á: 'a', ä: 'a', à: 'a', â: 'a', ã: 'a', å: 'a', ā: 'a', ă: 'a', ą: 'a',
  č: 'c', ć: 'c', ç: 'c',
  ď: 'd', đ: 'd',
  é: 'e', ě: 'e', ë: 'e', è: 'e', ê: 'e', ē: 'e', ė: 'e', ę: 'e',
  í: 'i', ï: 'i', ì: 'i', î: 'i', ī: 'i', į: 'i',
  ľ: 'l', ĺ: 'l', ł: 'l',
  ň: 'n', ń: 'n', ñ: 'n',
  ó: 'o', ö: 'o', ò: 'o', ô: 'o', õ: 'o', ō: 'o', ő: 'o', ø: 'o',
  ř: 'r', ŕ: 'r',
  š: 's', ś: 's', ș: 's', ß: 'ss',
  ť: 't', ț: 't',
  ú: 'u', ů: 'u', ü: 'u', ù: 'u', û: 'u', ū: 'u', ű: 'u',
  ý: 'y', ÿ: 'y',
  ž: 'z', ź: 'z', ż: 'z',
};

function tokenToLocalPart(token: string): string {
  const s = token.normalize('NFD').replace(/\p{M}/gu, '');
  let out = '';
  for (const ch of s) {
    const lc = ch.toLowerCase();
    if ((lc >= 'a' && lc <= 'z') || (lc >= '0' && lc <= '9')) {
      out += lc;
      continue;
    }
    if (lc === '-') {
      out += '-';
      continue;
    }
    if (LATIN1[lc] !== undefined) {
      out += LATIN1[lc];
      continue;
    }
  }
  return out;
}

export function suggestCreativedockEmail(fullName: string, domain: string): string | null {
  const full = fullName.trim().replace(/\s+/g, ' ');
  const parts = full.split(' ').filter(Boolean);
  if (parts.length < 2) return null;
  const first = tokenToLocalPart(parts[0]);
  const last = tokenToLocalPart(parts[parts.length - 1]);
  if (!first || !last) return null;
  const d = domain.trim().toLowerCase();
  if (!d) return null;
  return `${first}.${last}@${d}`;
}

const SIMPLE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

/** Dataset / template addresses that must not be sent to directory validation as real contacts. */
export function isWorkspaceContactPlaceholder(email: string): boolean {
  const e = email.trim().toLowerCase();
  if (!e) return true;
  if (e === 'email@company.com') return true;
  if (e.endsWith('@example.com') || e.endsWith('@example.org') || e.endsWith('@test.com')) return true;
  if (e === 'test@test.com' || e === 'user@user.com') return true;
  return false;
}

/** Prefer an explicit contact if it looks like email; otherwise guess from display name. */
export function pickWorkspaceEmailToValidate(
  contact: string | undefined,
  fullName: string,
  defaultDomain: string,
): string | null {
  const c = (contact || '').trim();
  if (SIMPLE_EMAIL.test(c) && !isWorkspaceContactPlaceholder(c)) {
    return c.toLowerCase();
  }
  return suggestCreativedockEmail(fullName, defaultDomain);
}
